import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const PendingApprovalsContext = createContext();

export const usePendingApprovals = () => {
  const context = useContext(PendingApprovalsContext);
  if (!context) {
    throw new Error('usePendingApprovals must be used within a PendingApprovalsProvider');
  }
  return context;
};

export const PendingApprovalsProvider = ({ children }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);
  const [user, setUser] = useState(null);
  const [clinicId, setClinicId] = useState(null);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user: u } } = await supabase.auth.getUser();
      setUser(u);
    };
    getCurrentUser();
  }, []);

  // Look up clinic once user is known
  useEffect(() => {
    if (!user) return;

    const fetchClinic = async () => {
      // Try clinic owner first
      const { data: clinic } = await supabase
        .from('clinics')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (clinic) {
        setClinicId(clinic.id);
        return;
      }

      // Fallback: vet user
      const { data: vet } = await supabase
        .from('vets')
        .select('clinic_id')
        .eq('user_id', user.id)
        .single();

      if (vet) {
        setClinicId(vet.clinic_id);
      }
    };

    fetchClinic();
  }, [user]);

  const fetchData = async () => {
    if (!clinicId) return;

    try {
      // Fetch requires_confirmation setting
      const { data: settings } = await supabase
        .from('settings_clinic_settings')
        .select('requires_confirmation')
        .eq('clinic_id', clinicId)
        .single();

      const manual = settings?.requires_confirmation || false;
      setRequiresConfirmation(manual);

      if (!manual) {
        setPendingCount(0);
        return;
      }

      // Count scheduled appointments
      const { data, error } = await supabase
        .from('appointments')
        .select('id')
        .eq('clinic_id', clinicId)
        .eq('status', 'scheduled')
        .is('deleted_at', null);

      if (error) {
        console.error('Failed to fetch pending count:', error);
        setPendingCount(0);
        return;
      }

      setPendingCount(data?.length || 0);
    } catch (err) {
      console.error('Failed to fetch pending approvals:', err);
      setPendingCount(0);
    }
  };

  useEffect(() => {
    if (clinicId) {
      fetchData();

      // Real-time subscription for appointment changes
      const subscription = supabase
        .channel('pending_approvals_changes')
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'appointments'
          },
          () => {
            fetchData();
          }
        )
        .subscribe();

      // Also listen for settings changes
      const settingsSub = supabase
        .channel('settings_changes_for_approvals')
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'settings_clinic_settings'
          },
          () => {
            fetchData();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
        settingsSub.unsubscribe();
      };
    }
  }, [clinicId]);

  const refreshPendingCount = () => {
    fetchData();
  };

  return (
    <PendingApprovalsContext.Provider value={{
      pendingCount,
      requiresConfirmation,
      refreshPendingCount
    }}>
      {children}
    </PendingApprovalsContext.Provider>
  );
};
